import { z } from 'zod';
import { getHttpClient } from '@/singleton-clients';
import { nanoidUUIDv4 } from '@/utils/id-utils';
import {
	createElasticube,
	getCSVSchemaDetails,
	getPerspectiveOid,
	addRelation,
} from '@/services/data-models/ecm-service';
import { inferRelations, type InferTableInput } from './infer-relations';
import { triggerFullBuild, pollBuild } from './rest-build';
import {
	buildCsvModelParamsSchema,
	type BuildCsvModelResult,
	type BuiltRelation,
	type BuiltTable,
	type CsvModelEngineContext,
} from './types';
import { TOOL_NAME_BUILD_CSV_MODEL, toEngineName } from '@/constants';
import { devDebug } from '@/utils/dev-debug';

interface DetectedColumn {
	columnName: string;
	dbType: number;
	size: number;
	precision: number;
	scale: number;
}

interface DetectedSchema {
	columns: DetectedColumn[];
}

interface AddDatasetResponse {
	data: { dataset: { oid: string } };
	errors?: Array<{ error?: { message?: string }; message?: string }>;
}

interface AddRelationResponse {
	data?: { relation?: { oid?: string } };
	errors?: Array<{ error?: { message?: string }; message?: string }>;
}

const TABLE_NAME_FROM_FILE = (originalName: string): string => {
	// "olist_order_items_dataset.csv" → "olist_order_items_dataset"
	// "Artist.csv" → "Artist"
	const stem = originalName.replace(/\.[^.]+$/, '');
	return stem || originalName;
};

/**
 * Optional snapshot accompanying a progress update. Lets the renderer surface
 * the table + relation diagram as soon as the model is wired but BEFORE the
 * physical cube build completes, instead of waiting for the slow build step.
 */
export interface BuildCsvModelProgressSnapshot {
	tables?: BuiltTable[];
	relations?: BuiltRelation[];
}

export async function buildCsvModelEngine(
	params: z.infer<typeof buildCsvModelParamsSchema>,
	context: CsvModelEngineContext,
	onProgress?: (
		phase: string,
		detail?: string,
		snapshot?: BuildCsvModelProgressSnapshot,
	) => void,
): Promise<BuildCsvModelResult> {
	const {
		uploadedFiles,
		modelTitle,
		inferRelations: doInferRelations,
	} = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_BUILD_CSV_MODEL),
		modelTitle,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	// Phase 1 — detect schemas in parallel
	onProgress?.('detect_schemas', `${uploadedFiles.length} files`);
	const detected = await Promise.all(
		uploadedFiles.map(async (f, i) => {
			const res = (await getCSVSchemaDetails({
				filePath: f.storagePath,
				fileName: f.fileName,
			})) as DetectedSchema | null;
			const cols = res?.columns ?? [];
			if (cols.length === 0) {
				throw new Error(
					`Schema detection returned 0 columns for ${f.originalName} (${f.storagePath})`,
				);
			}
			onProgress?.('detect_schemas', `${i + 1}/${uploadedFiles.length}`);
			return { file: f, columns: cols };
		}),
	);

	// Phase 2 — create elasticube
	onProgress?.('create_elasticube', modelTitle);
	const cube = await createElasticube({ title: modelTitle });
	if (!cube?.oid) {
		throw new Error('createElasticube returned no oid');
	}
	const datamodelOid = cube.oid;

	// Phase 3 — get default perspective oid
	onProgress?.('lookup_perspective');
	const perspResp = await getPerspectiveOid({ elasticubeOid: datamodelOid });
	const perspectiveOid =
		((perspResp as unknown as
			| Array<{ oid: string }>
			| { oid: string }
			| null) &&
			(Array.isArray(perspResp)
				? perspResp[0]?.oid
				: (perspResp as { oid?: string } | null)?.oid)) ||
		datamodelOid;

	// Phase 4 — add ONE dataset with all tables (multi-file CSV connection)
	onProgress?.('add_dataset', `${detected.length} tables`);
	const tableInfo: BuiltTable[] = [];
	const tableInputs = detected.map(({ file, columns }) => {
		const tableOid = nanoidUUIDv4();
		const dataColumns = columns.map((c) => ({
			id: c.columnName,
			name: c.columnName,
			type: c.dbType,
			oid: nanoidUUIDv4(),
			size: c.size,
			precision: c.precision,
			scale: c.scale,
			hidden: false,
			indexed: true,
		}));
		const tableName = TABLE_NAME_FROM_FILE(file.originalName);
		// Sisense identifies the file inside the multi-file CSV dataset by
		// `table.id` matching the storage filename in `parameters.files`. Using
		// the original upload name here causes the build to fail in ~20s
		// because the cube can't find the file. Always derive from storagePath.
		const storageFileName =
			file.storagePath.split('/').pop() ?? file.fileName;
		tableInfo.push({
			oid: tableOid,
			name: tableName,
			datasetOid: '', // filled in after addDataset
			columns: dataColumns.map((c) => ({
				oid: c.oid,
				name: c.name,
				type: c.type,
			})),
		});
		return {
			id: storageFileName,
			name: tableName,
			oid: tableOid,
			hidden: false,
			schemaName: file.storagePath,
			buildBehavior: { type: 'sync' },
			columns: dataColumns,
			configOptions: {
				delimiter: ',',
				hasHeader: true,
				ignoreMissingColumns: false,
				excludeIfBegins: '',
				excludeIfContains: '',
				stringQuote: '"',
				ignoreFirstRows: '',
				culture: 'en-US',
				columnsDateTimePatterns: '{}',
			},
			tupleTransformations: [],
		};
	});

	const allFilePaths = uploadedFiles.map((f) => f.storagePath);
	const addDatasetBody = {
		operationName: 'addDataset',
		variables: {
			elasticubeOid: datamodelOid,
			connection: {
				provider: 'CSV',
				schema: null,
				fileName: null,
				parameters: { files: allFilePaths, unionAll: 'false' },
				uiParams: {},
				globalTableConfigOptions: { datesPicker: {} },
			},
			tables: tableInputs,
			suggestedOid: nanoidUUIDv4(),
			schemaName: null,
			perspectiveOid,
			updatedPerspectiveData: { relationsTables: [] },
		},
		query: `mutation addDataset($elasticubeOid: UUID!, $connection: ConnectionInput!, $tables: [TableInput!]!, $name: String, $suggestedOid: UUID!, $database: String, $schemaName: String, $perspectiveOid: UUID!, $updatedPerspectiveData: PerspectiveInput) {
			markTransactional(elasticubeOid: $elasticubeOid)
			dataset: addDatasetToElasticube(elasticubeOid: $elasticubeOid, connection: $connection, tables: $tables, name: $name, suggestedOid: $suggestedOid, database: $database, schemaName: $schemaName) { oid }
			updatePerspective(perspectiveOid: $perspectiveOid, updatedPerspectiveData: $updatedPerspectiveData)
		}`,
	};

	const addDsResp = await httpClient.post<AddDatasetResponse>(
		`/api/v2/ecm/`,
		addDatasetBody,
	);
	if (addDsResp?.errors?.length) {
		throw new Error(
			`addDataset failed: ${JSON.stringify(addDsResp.errors)}`,
		);
	}
	const datasetOid = addDsResp?.data?.dataset?.oid;
	if (!datasetOid) {
		throw new Error('addDataset returned no dataset oid');
	}
	for (const t of tableInfo) t.datasetOid = datasetOid;

	// Tables are now wired — surface them so the renderer can paint the
	// diagram while relations are being inferred and the cube is built.
	onProgress?.('add_dataset', `${tableInfo.length} tables`, {
		tables: tableInfo,
	});

	// Phase 5 — infer + add relations
	const relations: BuiltRelation[] = [];
	if (doInferRelations) {
		onProgress?.('add_relations', 'inferring', { tables: tableInfo });
		const inferInput: InferTableInput[] = tableInfo.map((t) => ({
			name: t.name,
			columns: t.columns.map((c) => ({ name: c.name })),
		}));
		const inferred = inferRelations(inferInput);
		onProgress?.('add_relations', `${inferred.length} inferred`, {
			tables: tableInfo,
		});
		const tableByName = new Map(tableInfo.map((t) => [t.name, t]));
		for (const r of inferred) {
			const src = tableByName.get(r.sourceTable);
			const tgt = tableByName.get(r.targetTable);
			if (!src || !tgt) continue;
			const srcColOid = src.columns.find(
				(c) => c.name === r.sourceColumn,
			)?.oid;
			const tgtColOid = tgt.columns.find(
				(c) => c.name === r.targetColumn,
			)?.oid;
			if (!srcColOid || !tgtColOid) continue;

			const relResp = (await addRelation({
				elasticubeOid: datamodelOid,
				source: {
					dataset: src.datasetOid,
					table: src.oid,
					column: srcColOid,
				},
				target: {
					dataset: tgt.datasetOid,
					table: tgt.oid,
					column: tgtColOid,
				},
			})) as AddRelationResponse | null;
			const relOid = relResp?.data?.relation?.oid;
			if (relOid) {
				relations.push({
					oid: relOid,
					sourceTable: r.sourceTable,
					sourceColumn: r.sourceColumn,
					targetTable: r.targetTable,
					targetColumn: r.targetColumn,
				});
				onProgress?.(
					'add_relations',
					`${relations.length}/${inferred.length}`,
					{ tables: tableInfo, relations: [...relations] },
				);
			}
		}
	}

	// Phase 6 — REST build trigger + poll
	onProgress?.('build', 'starting', {
		tables: tableInfo,
		relations: [...relations],
	});
	const { buildOid } = await triggerFullBuild(datamodelOid, httpClient);
	const { status, durationMs } = await pollBuild(buildOid, {
		intervalMs: 5_000,
		timeoutMs: 600_000,
		httpClient,
		onUpdate: (s) =>
			onProgress?.('build', s, {
				tables: tableInfo,
				relations: [...relations],
			}),
	});

	return {
		datamodelOid,
		title: modelTitle,
		tables: tableInfo,
		relations,
		buildOid,
		buildStatus: status,
		buildDurationMs: durationMs,
	};
}
